import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const SEVERITY_TONE = {
  info: { chip: 'bg-white/10 text-white/70 border-white/10', dot: 'bg-white/40' },
  warn: { chip: 'bg-amber-500/15 text-amber-300 border-amber-500/30', dot: 'bg-amber-400' },
  critical: { chip: 'bg-rose-500/15 text-rose-300 border-rose-500/30', dot: 'bg-rose-500' },
};

const KIND_LABEL = {
  auth_login_success: 'Login (success)',
  auth_login_failed: 'Login (failed)',
  auth_register: 'Register',
  admin_area_forbidden: 'Non-admin blocked at /api/admin/*',
  admin_protect_block: 'Admin-account protection triggered',
  admin_role_change: 'Role changed',
};

export const SecuritySection = () => {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [email, setEmail] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (kind !== 'all') params.set('kind', kind);
      if (severity !== 'all') params.set('severity', severity);
      if (email.trim()) params.set('email', email.trim());
      const [e, s] = await Promise.all([
        api.get(`/admin/security-events?${params.toString()}`).then(r => r.data).catch(() => []),
        api.get('/admin/security-events/summary').then(r => r.data).catch(() => null),
      ]);
      setEvents(e);
      setSummary(s);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind, severity]);

  const kpis = [
    { label: 'Events (24h)', value: summary?.total_24h ?? '—', hue: 'text-sky-300' },
    { label: 'Critical (24h)', value: summary?.critical_24h ?? '—', hue: 'text-rose-300' },
    { label: 'Failed logins (24h)', value: summary?.failed_logins_24h ?? '—', hue: 'text-amber-300' },
    { label: 'Admin forbidden (24h)', value: summary?.admin_forbidden_24h ?? '—', hue: 'text-fuchsia-300' },
    { label: 'Admin protect (24h)', value: summary?.admin_protect_24h ?? '—', hue: 'text-orange-300' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-security-section">
      <div>
        <h2 className="text-2xl font-display font-bold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-400" /> Security & audit
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--jw-text-muted)' }}>
          Every admin login attempt, forbidden hit on <code>/api/admin/*</code>, and admin-protection block is captured here.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="admin-security-kpis">
        {kpis.map((k) => (
          <Card key={k.label} className="jw-card p-4">
            <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--jw-text-subtle)' }}>{k.label}</p>
            <p className={`font-display text-2xl font-bold mt-1 ${k.hue}`}>{k.value}</p>
          </Card>
        ))}
      </div>

      <Card className="jw-card p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 opacity-60" />
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder="Filter by email..."
              data-testid="admin-security-email-input"
              className="flex-1"
            />
          </div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-52" data-testid="admin-security-kind-select"><SelectValue placeholder="All events" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              <SelectItem value="auth_login_failed">Failed logins</SelectItem>
              <SelectItem value="auth_login_success">Successful logins</SelectItem>
              <SelectItem value="auth_register">Registrations</SelectItem>
              <SelectItem value="admin_area_forbidden">Admin forbidden</SelectItem>
              <SelectItem value="admin_protect_block">Admin protection</SelectItem>
              <SelectItem value="admin_role_change">Role changes</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="w-40" data-testid="admin-security-severity-select"><SelectValue placeholder="All severities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warn">Warn</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={load} disabled={loading} data-testid="admin-security-refresh-button">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </Card>

      <Card className="jw-card overflow-hidden" data-testid="admin-security-events-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left" style={{ color: 'var(--jw-text-subtle)' }}>
                <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider">When</th>
                <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider">Event</th>
                <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider">IP</th>
                <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider">Detail</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--jw-text-muted)' }}>
                  {loading ? 'Loading...' : 'No events match your filters.'}
                </td></tr>
              )}
              {events.map((e) => {
                const tone = SEVERITY_TONE[e.severity] || SEVERITY_TONE.info;
                return (
                  <tr key={e.id} className="border-t" style={{ borderColor: 'var(--jw-border)' }} data-testid={`security-event-row-${e.id}`}>
                    <td className="px-4 py-3 align-top whitespace-nowrap" style={{ color: 'var(--jw-text-muted)' }}>
                      {new Date(e.at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                        <span className="font-medium">{KIND_LABEL[e.kind] || e.kind}</span>
                      </div>
                      <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${tone.chip}`}>
                        {e.severity}{e.success ? ' · ok' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top break-all">{e.email || '—'}</td>
                    <td className="px-4 py-3 align-top whitespace-nowrap font-mono text-xs">{e.ip || '—'}</td>
                    <td className="px-4 py-3 align-top max-w-md" style={{ color: 'var(--jw-text-muted)' }}>
                      {e.detail || (e.path ? <code className="text-xs">{e.path}</code> : '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};


export default SecuritySection;
